import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { LANG_PATH, LANGS_PATH, userLangPath } from "../../functions/src/admin/paths";
import { SchoolLangsConfig } from "../../functions/src/types";
import { auth, mainDatabase } from "./firebase";
import { useDatabaseVal } from "./firebaseHooks";
import { Lang, langPipeIndex, LANGUAGE_OPTIONS, LANGUAGES, localeFromLang as resolveLocaleFromLang, SUPPORTED_LANGUAGES, type Language, type LocaleUsage, type TermsDoc } from "./langConfig";

export {
    formatGreekUpper,
    LANG_ASSETS, langAsset,
    langPipeIndex, LANGUAGE_LOCALES,
    LANGUAGE_OPTIONS,
    LANGUAGES,
    localeFromLang as localeFromLangCode,
    SUPPORTED_LANGUAGES,
    termsUrl,
    uppercaseForLang,
} from "./langConfig";
export type { Language, LocaleUsage, TermsDoc };

export function getApplicableLangKeys(catalogKeys: string[], stored: SchoolLangsConfig['applicableLangs']): string[] {
    if (!stored) {
        return catalogKeys;
    }
    return catalogKeys.filter((code) => !!stored[code]);
}

/** Short label for compact header toggles. */
export function langShortLabel(code: Language | string): string {
    if (code in LANGUAGE_OPTIONS) {
        return LANGUAGE_OPTIONS[code as Language].shortLabel;
    }
    return String(code).toUpperCase().slice(0, 2);
}

export function resolveSchoolLang(
    requested: string | null | undefined,
    applicableLangs: Language[],
    fallback?: Language
): Language {
    const pool = applicableLangs.length ? applicableLangs : LANGUAGES;
    if (requested && pool.includes(requested as Language)) {
        return requested as Language;
    }
    if (fallback && pool.includes(fallback)) {
        return fallback;
    }
    return pool[0];
}

export function useSchoolLangs(): { langs: Language[]; defaultLang?: Language; loaded: boolean } {
    const [schoolLangsVal, schoolLangsSnapshot] = useDatabaseVal<SchoolLangsConfig | null>(LANG_PATH);
    const [catalog = {}] = useDatabaseVal<Record<string, unknown>>(auth.currentUser ? LANGS_PATH : '');
    // School `langs/` is public on the school DB; main-DB `resources/langs` needs auth — do not block on catalog.
    const loaded = !!schoolLangsSnapshot;
    const catalogKeys = useMemo(() => {
        const keys = Object.keys(catalog);
        return keys.length ? keys : [...LANGUAGES];
    }, [catalog]);
    const langs = useMemo((): Language[] => {
        if (!loaded) {
            return [];
        }
        const hasExplicitApplicable = schoolLangsVal?.applicableLangs != null;
        if (hasExplicitApplicable) {
            const explicit = Object.keys(schoolLangsVal!.applicableLangs!)
                .filter((code) => !!schoolLangsVal!.applicableLangs![code])
                .filter((code) => code in SUPPORTED_LANGUAGES) as Language[];
            if (explicit.length) {
                return explicit;
            }
        }
        const applicableKeys = getApplicableLangKeys(catalogKeys, schoolLangsVal?.applicableLangs);
        const supported = applicableKeys.filter((code) => code in SUPPORTED_LANGUAGES) as Language[];
        if (supported.length) {
            return supported;
        }
        const legacy = catalogKeys.filter((code) => code in SUPPORTED_LANGUAGES) as Language[];
        return legacy.length ? legacy : LANGUAGES;
    }, [loaded, catalogKeys, schoolLangsVal?.applicableLangs]);
    const defaultLang = schoolLangsVal?.defaultLang && langs.includes(schoolLangsVal.defaultLang as Language)
        ? schoolLangsVal.defaultLang as Language
        : undefined;

    return {
        langs,
        defaultLang,
        loaded,
    };
}

/** Compact Ελ/En (etc.) toggle; hidden until school langs load and more than one applies. */
export function LangToggle({
    className = 'lang',
    edgeStyles,
    /** When set (e.g. maintenance before school langs load), always show these codes. */
    langsOverride,
}: {
    className?: string;
    edgeStyles?: { first?: React.CSSProperties; last?: React.CSSProperties };
    langsOverride?: Language[];
}) {
    const [lang, setLang] = useContext(langContext);
    const { langs: schoolLangs, loaded } = useSchoolLangs();
    const langs = langsOverride ?? (loaded ? schoolLangs : []);

    if (!langsOverride && (!loaded || langs.length <= 1)) {
        return null;
    }
    if (langs.length <= 1) {
        return null;
    }

    return (
        <span className={className}>
            {langs.map((code, index) => (
                <React.Fragment key={code}>
                    {index > 0 && <span className="lang-sep" style={{ margin: '0 10px' }}>|</span>}
                    <span
                        className={`lang-option${lang === code ? ' selected' : ''}`}
                        onClick={() => setLang(code)}
                        style={index === 0 ? edgeStyles?.first : index === langs.length - 1 ? edgeStyles?.last : undefined}
                    >
                        {langShortLabel(code)}
                    </span>
                </React.Fragment>
            ))}
        </span>
    );
}

const STARTING_LANG = ((localStorage.getItem('lang') ?? LANGUAGES[0])) as Language;

export const langContext = createContext<[Language, LanguageSet]>([STARTING_LANG, _ => null]);


export default function Lang({ children }: { children: string }) {
    return <>{useLang(children)}</>
}

/** Legacy bilingual pipe: `"Greek|English|…"` segments follow `SUPPORTED_LANGUAGES` insertion order. */
export function useLang(message: string, lang?: Language) {
    lang = lang ?? useContext(langContext)[0];
    const parts = message?.split('|') ?? [];
    return parts[langPipeIndex(lang)] ?? parts[0] ?? '';
}

/** Resolves locale from `lang` or current `langContext` when `lang` is omitted. */
export function localeFromLang(lang?: Language, usage: LocaleUsage = "date"): string {
    return resolveLocaleFromLang(lang ?? useContext(langContext)[0], usage);
}

export type LangMap<T> = Record<Language, T>;
export function useLangMap<T>(langs: LangMap<T>): T {
    const [lang] = useContext(langContext);
    return langs[lang];
}

/**
 * RTDB/admin `Lang` field: requested language first, then other supported languages in catalog order.
 */
export function getLangValue(langField: Lang | undefined, requestedLang: Language): string {
    if (!langField) return '';

    const direct = langField[requestedLang];
    if (typeof direct === 'string' && direct.trim()) {
        return direct.trim();
    }

    for (const code of LANGUAGES) {
        if (code === requestedLang) continue;
        const fallback = langField[code];
        if (typeof fallback === 'string' && fallback.trim()) {
            return fallback.trim();
        }
    }

    return '';
}

/** True when `langField` has a non-empty string for at least one {@link LANGUAGES} code. */
export function hasLangFieldContent(langField: Lang | undefined): boolean {
    if (!langField) return false;
    return LANGUAGES.some((code) => {
        const v = langField[code];
        return typeof v === "string" && v.trim().length > 0;
    });
}

/**
 * Use only in base app
 */
export function useLanguageProvider(): [Language, LanguageSet] {

    const [lang, setLang] = useState<Language>(() => STARTING_LANG);

    const storeSetLang = useCallback((lang: Language) => {
        setLang(lang);
        localStorage.setItem('lang', lang);
        if (!!auth?.currentUser) {
            const { uid, email } = auth.currentUser;
            mainDatabase.ref().child(userLangPath(uid)).set(lang);
        }
    }, [setLang]);

    return useMemo(() => [lang, storeSetLang], [lang, setLang]);
}

type LanguageSet = React.Dispatch<React.SetStateAction<Language>>;
